import React, { useEffect, useState, useMemo, useRef } from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import api from '../../../config/axiosConfig';
import pdfIcon from "../../../assets/pdf-icon.png";
import ConfirmDeleteModal from "../confirmdeletemodal";
import { AgGridReact } from 'ag-grid-react';
import loaderGif from "../../../assets/loader.gif";

const Account = () => {
    const [formData, setFormData] = useState({
        name: '',
        stateId: '',
        countryId: '',
        idProofTypeId: '',
        idProofNumber: '',
        idProofDocument: '',
        address: '',
        isOrganization: '',
        GST: '',
        phone: '',
        email: '',
        isactive: ''
    });

    const [isEditMode, setIsEditMode] = useState(false);
    const [countries, setCountries] = useState();
    const [states, setStates] = useState();
    const [idProofTypes, setIdProofTypes] = useState();
    const [file, setFile] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loader, setLoader] = useState(false);
    const [rowData, setRowData] = useState([]);

    const getData = async () => {
        try {
            const response = await api.get('/accounts');
            setRowData(response.data);
            const countries = await api.get('/countries');
            setCountries(countries.data);
            const idProofTypes = await api.get('/idProofTypes');
            setIdProofTypes(idProofTypes.data)

        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        getData();
    }, []);

    const gridRef = useRef();

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const columnDefs = [
        { headerName: 'ID', field: 'id', sort: "desc", filter: 'agNumberColumnFilter' },
        { headerName: 'Name', field: 'name', filter: 'agTextColumnFilter' },
        { headerName: 'CountryID', field: 'countryId', filter: 'agNumberColumnFilter' },
        { headerName: 'State ID', field: 'stateId', filter: 'agNumberColumnFilter' },
        { headerName: 'ID Proof Type Id', field: 'idProofTypeId', filter: 'agNumberColumnFilter' },
        { headerName: 'ID Proof Number', field: 'idProofNumber', filter: 'agNumberColumnFilter' },
        {
            headerName: 'idProofDocument',
            field: 'idProofDocument',
            cellRenderer: (params) => {
                const filePath = params.data.idProofDocument.replace('uploads/idProofDocuments/', '');
                const mediaUrl = `${process.env.REACT_APP_WS_URL}/api/accounts/idProofDocuments${filePath}`;

                if (!filePath || mediaUrl === `${process.env.REACT_APP_WS_URL}/api/accounts/idProofDocuments`) {
                    return (
                        <div style={{ textAlign: 'center', color: 'gray' }}>
                            No Document Present
                        </div>
                    );
                }

                // Check if the media URL is a video
                const isVideo = mediaUrl && (mediaUrl.includes('.mp4') || mediaUrl.includes('.webm'));

                if (isVideo) {
                    // Video renderer
                    return (
                        <video
                            src={mediaUrl}
                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                            muted
                        />
                    );
                } else if (mediaUrl.includes('.pdf')) {
                    // PDF renderer
                    return (
                        <a href={mediaUrl} target="_blank" rel="noopener noreferrer">
                            <img
                                src={pdfIcon}
                                alt="PDF"
                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                            />
                        </a>
                    );
                } else {
                    // Image renderer
                    return (
                        <img
                            src={mediaUrl}
                            alt="media"
                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                        />
                    );
                }
            },
        },
        { headerName: 'Primary Mobile', field: 'phone', filter: 'agNumberColumnFilter' },
        { headerName: 'Primary Email', field: 'email', filter: 'agTextColumnFilter' },
        { headerName: 'Address', field: 'address', filter: 'agTextColumnFilter' },
        { headerName: 'Is Organization', field: 'isOrganization' },
        { headerName: 'GST', field: 'GST', filter: 'agNumberColumnFilter' },
        { headerName: 'Is Active', field: 'isactive' },
        { headerName: 'Created By', field: 'createdby', filter: 'agTextColumnFilter' },
        { headerName: 'Created At', field: 'createddate', filter: 'agDateColumnFilter' },
        { headerName: 'Updated By', field: 'modifiedby', filter: 'agTextColumnFilter' },
        { headerName: 'Updated At', field: 'modifieddate', filter: 'agDateColumnFilter' },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: (params) => (
                <Button
                    variant="danger"
                    className="btn-sm"
                    onClick={() => handleDelete(params.data)}
                >
                    <i className="bi bi-trash3"></i>
                </Button>
            ),
        },
    ];

    const sideBar = useMemo(() => {
        return {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                },
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel',
                }
            ],
            defaultToolPanel: '',
        };
    }, []);

    const datasource = {
        getRows: async function (params) {
            try {
                const { request } = params;
                const response = await api.post('/accounts/ag', { ...request });
                const { rows, lastRow } = response.data;

                // Send data back to AG Grid
                params.successCallback(rows, lastRow);
            } catch (error) {
                console.error("Error fetching rows:", error);
                params.failCallback();
            }
        }
    };

    const gridOptions = {
        rowModelType: 'serverSide',
        columnDefs: columnDefs,
        rowHeight: 60,

        defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            floatingFilter: true,
            enableValue: true,
            enableRowGroup: true,
            enablePivot: true,
            filterParams: {
                buttons: ["reset"],
                closeOnApply: true,
            },
        },

        sideBar: sideBar,
        rowGroupPanelShow: 'always',
        rowSelection: 'single',
        columnHoverHighlight: true,
        suppressRowGroupHidesColumns: true,
        cacheBlockSize: 20
    }

    // Attach the datasource when the grid is ready
    const onGridReady = (params) => {
        params.api.setServerSideDatasource(datasource);
    };

    const onRowSelected = (event) => {
        if (event.node.isSelected()) {
            const selectedRow = event.data;

            setFormData({
                id: selectedRow.id,
                name: selectedRow.name,
                stateId: selectedRow.stateId,
                countryId: selectedRow.countryId,
                idProofTypeId: selectedRow.idProofTypeId,
                idProofNumber: selectedRow.idProofNumber,
                idProofDocument: selectedRow.idProofDocument,
                phone: selectedRow.phone,
                email: selectedRow.email,
                address: selectedRow.address,
                isOrganization: selectedRow.isOrganization,
                GST: selectedRow.GST,
                isactive: formData.isactive
            });
            setIsEditMode(true);  // Switch to edit mode
            setSelectedRow(selectedRow)
        }
    };

    const handleCountryChange = async (e) => {
        const selectedCountryId = Number(e.target.value); // Get the selected item type ID
        setFormData({ ...formData, countryId: selectedCountryId });
        const states = await api.get(`/states/getstatebycountry/${selectedCountryId}`);
        setStates(states.data);

    };

    // Handle form submission
    const handleAddData = async (e) => {
        e.preventDefault();
        if (formData.name) {
            try {
                const url = isEditMode ? `/accounts/${formData.id}` : '/accounts';
                const method = isEditMode ? 'put' : 'post';
                const formDataToSend = new FormData();
                formDataToSend.append('name', formData.name);
                formDataToSend.append('stateId', formData.stateId || '');
                formDataToSend.append('countryId', formData.countryId || '');
                formDataToSend.append('idProofTypeId', formData.idProofTypeId || '');
                formDataToSend.append('idProofNumber', formData.idProofNumber || '');
                formDataToSend.append('address', formData.address);
                formDataToSend.append('isOrganization', formData.isOrganization);
                formDataToSend.append('GST', formData.GST);
                formDataToSend.append('phone', formData.phone);
                formDataToSend.append('email', formData.email);
                formDataToSend.append('isactive', formData.isactive);
                if (file) formDataToSend.append('idProofDocument', file);

                // Make the Axios call
                await api({
                    method,
                    url,
                    data: formDataToSend,
                });
            } catch (error) {
                console.error('Error:', error);
            }
            // Clear the form
            setFormData({ id: null, name: '', isactive: true, stateId: '', countryId: '', idProofTypeId: '', idProofNumber: '', idProofDocument: '', address: '', isOrganization: '', GST: '', phone: '', email: '' });
            setIsEditMode(false); // Reset to add mode
            setLoader(false);
            gridRef.current.api.refreshServerSideStore({ purge: true });
        } else {
            alert("Please fill out the name field.");
        }
    };

    // Reset form for new entry
    const handleNewEntry = () => {
        setFormData({ id: null, name: '', isactive: true, stateId: '', countryId: '', idProofTypeId: '', idProofNumber: '', idProofDocument: '', address: '', isOrganization: '', GST: '', phone: '', email: '' });
        setFile([]);
        setIsEditMode(false);  // Switch to add mode
    };

    const handleDelete = () => {
        setShowModal(true);
    };

    const confirmDelete = async () => {
        try {
            await api.delete(`/accounts/${selectedRow.id}`);
            setShowModal(false);
            gridRef.current.api.refreshServerSideStore({ purge: true });
        } catch (error) {
            console.error('Error deleting idproof document:', error);
        }
    };

    return (
        <>
            <Container fluid>
                <Row className="mt-4">
                    <h6 className="text-start">Accounts List</h6>
                    <div className="ag-theme-alpine" style={{ height: 400, width: '70%' }}>
                        <AgGridReact
                            ref={gridRef}
                            gridOptions={gridOptions}
                            onGridReady={onGridReady}
                            onRowSelected={onRowSelected}
                        />
                    </div>
                    <div style={{ width: '30%', height: '400px', border: '1px solid grey' }}>
                        {selectedRow && selectedRow.idProofDocument ? (
                            selectedRow.idProofDocument.includes('.mp4') || selectedRow.idProofDocument.includes('.webm') ? (
                                <video
                                    src={`${process.env.REACT_APP_WS_URL}/api/accounts/idProofDocuments${selectedRow.idProofDocument.replace('uploads/idProofDocuments/', '')}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '5px' }}
                                    controls
                                />
                            ) : selectedRow.idProofDocument.includes('.pdf') ? (
                                <iframe
                                    src={`${process.env.REACT_APP_WS_URL}/api/accounts/idProofDocuments${selectedRow.idProofDocument.replace('uploads/idProofDocuments/', '')}`}
                                    style={{ width: '100%', height: '100%', borderRadius: '5px' }}
                                    title="PDF Viewer"
                                />
                            ) : (
                                <img
                                    src={`${process.env.REACT_APP_WS_URL}/api/accounts/idProofDocuments${selectedRow.idProofDocument.replace('uploads/idProofDocuments/', '')}`}
                                    alt="idProofDocument"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '5px' }}
                                />
                            )
                        ) : null}
                    </div>
                </Row>
                <Row className="mt-4 border shadow p-4">
                    {loader && (
                        <div className="loader-overlay">
                            <img src={loaderGif} alt="Loading..." className="loader" />
                        </div>
                    )}
                    <Col md={12}>
                        <h6 className="text-start">{isEditMode ? "Edit Account" : "Add Account"}</h6>
                        <Form onSubmit={handleAddData}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="me-3">Name:</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            placeholder="Enter name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Check
                                            type="checkbox"
                                            name="isactive"
                                            label="Is Active"
                                            checked={formData.isactive}
                                            onChange={handleChange}
                                            className="ms-4"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Check
                                            type="checkbox"
                                            name="isOrganization"
                                            label="Is Organization"
                                            checked={formData.isOrganization}
                                            onChange={handleChange}
                                            className="ms-4"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Country</Form.Label>
                                        <InputGroup>
                                            <Form.Select
                                                name="countryId"
                                                value={formData.countryId}
                                                onChange={handleCountryChange}
                                            >
                                                <option value="">Select Country</option>
                                                {countries && countries.map((country) => (
                                                    <option key={country.id} value={country.id}>
                                                        {country.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>State</Form.Label>
                                        <InputGroup>
                                            <Form.Select
                                                name="stateId"
                                                value={formData.stateId}
                                                onChange={(e) => setFormData({ ...formData, stateId: Number(e.target.value) })}
                                            >
                                                <option value="">Select a State</option>
                                                {states && states.map((state) => (
                                                    <option key={state.id} value={state.id}>
                                                        {state.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Primary Mobile</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="Number"
                                                name="phone"
                                                placeholder="Enter Mobile Number"
                                                value={formData.phone}
                                                onChange={handleChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Primary Email <span style={{ color: 'red' }}></span></Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="Email"
                                                name="email"
                                                placeholder="Enter Primary Email Address"
                                                value={formData.email}
                                                onChange={handleChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Address <span style={{ color: 'red' }}></span></Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="text"
                                                name="address"
                                                placeholder="Enter Address"
                                                value={formData.address}
                                                onChange={handleChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>GST</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="text"
                                                name="GST"
                                                placeholder="Enter GST"
                                                value={formData.GST}
                                                onChange={handleChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label>Id Proof Type<span style={{ color: 'red' }}></span></Form.Label>
                                        <InputGroup>
                                            <Form.Select
                                                name="idProofTypeId"
                                                value={formData.idProofTypeId}
                                                onChange={(e) => setFormData({ ...formData, idProofTypeId: Number(e.target.value) })}
                                            >
                                                <option value="">Select Id Proof Type</option>
                                                {idProofTypes && idProofTypes.map((idProofType) => (
                                                    <option key={idProofType.id} value={idProofType.id}>
                                                        {idProofType.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={5}>
                                    <Form.Group>
                                        <Form.Label>Id Proof Number<span style={{ color: 'red' }}></span></Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="text"
                                                name="idProofNumber"
                                                placeholder="Enter Id Proof Number"
                                                value={formData.idProofNumber}
                                                onChange={handleChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={5}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Upload Id Proof Document</Form.Label>
                                        <Form.Control
                                            type="file"
                                            onChange={handleFileChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Button variant="primary" type="submit">
                                {isEditMode ? "Update" : "Add"}
                            </Button>
                            {isEditMode ?
                                <Button variant="secondary" className="ms-3" onClick={handleNewEntry}>
                                    Create New Entry
                                </Button>
                                : ''
                            }

                        </Form>
                    </Col>
                </Row>

            </Container>

            {/* Modal for confirming deletion */}
            <ConfirmDeleteModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={confirmDelete}
                title="Confirm Deletion"
                body={`Are you sure you want to delete ${selectedRow?.name}?`}
                confirmText="Delete"
            />

        </>
    );
};



export default Account;